import ExcelJS from 'exceljs';
import brandDesktopLogoUrl from '../assets/branding/png/onemart-logo.png';

const COLOR = {
  signatureFill: 'FACC15',
  signatureFont: '000000',
  headerFill: '0F172A',
  headerFont: 'FFFFFF',
  border: 'CBD5E1',
  bodyText: '1F2937',
  altFill: 'F8FAFC',
};

const HEADER_LOGO_WIDTH = 170;
const HEADER_LOGO_HEIGHT = 29;
const HEADER_LOGO_TOP_OFFSET = 0.32;
const HEADER_LOGO_LEFT_OFFSET = 0.18;

const toArgb = (rgb) => `FF${String(rgb || '').replace(/^#/, '').toUpperCase().padStart(6, '0')}`;

const normalizeCell = (value) => {
  if (value === undefined || value === null) return '';
  return value;
};

const normalizeSheetName = (value, index) => {
  const fallback = `Sheet ${index + 1}`;
  const base = String(value || fallback)
    .replace(/[\\/?*:]/g, ' ')
    .replace(/\[/g, ' ')
    .replace(/\]/g, ' ')
    .trim();
  return (base || fallback).slice(0, 31);
};

const encodeColumnName = (index) => {
  let current = Number(index) || 1;
  let column = '';

  while (current > 0) {
    const remainder = (current - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    current = Math.floor((current - remainder - 1) / 26);
  }

  return column;
};

const makeBorder = () => ({
  top: { style: 'thin', color: { argb: toArgb(COLOR.border) } },
  right: { style: 'thin', color: { argb: toArgb(COLOR.border) } },
  bottom: { style: 'thin', color: { argb: toArgb(COLOR.border) } },
  left: { style: 'thin', color: { argb: toArgb(COLOR.border) } },
});

const arrayBufferToBase64 = (buffer) => {
  if (!buffer) return '';

  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return typeof btoa === 'function' ? btoa(binary) : binary;
};

const assetUrlToBase64 = async (assetUrl) => {
  if (typeof window === 'undefined' || !assetUrl) return null;

  try {
    const response = await fetch(assetUrl);
    if (!response.ok) return null;

    return arrayBufferToBase64(await response.arrayBuffer());
  } catch (error) {
    console.warn('Brand image load failed:', error);
    return null;
  }
};

const prepareBrandingAssets = async () => {
  const logoBase64 = await assetUrlToBase64(brandDesktopLogoUrl);
  return { logoBase64 };
};

const styleBandCell = (cell) => {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: toArgb(COLOR.signatureFill) },
  };
  cell.border = makeBorder();
  cell.font = {
    bold: true,
    color: { argb: toArgb(COLOR.signatureFont) },
    size: 11,
  };
  cell.alignment = {
    horizontal: 'right',
    vertical: 'bottom',
    wrapText: true,
  };
};

const formatTimestamp = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const styleHeaderRow = (row) => {
  row.height = 24;
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: toArgb(COLOR.headerFill) },
    };
    cell.border = makeBorder();
    cell.font = {
      bold: true,
      color: { argb: toArgb(COLOR.headerFont) },
      size: 10,
    };
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
  });
};

const styleDataRow = (row, rowIndex, normalizedColumns) => {
  const isAlt = rowIndex % 2 === 1;

  row.eachCell({ includeEmpty: true }, (cell, cellNumber) => {
    const value = cell.value;
    cell.border = makeBorder();
    cell.font = {
      color: { argb: toArgb(COLOR.bodyText) },
      size: 10,
    };
    cell.alignment = {
      horizontal: typeof value === 'number' ? 'right' : 'left',
      vertical: 'middle',
      wrapText: true,
    };

    if (isAlt) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: toArgb(COLOR.altFill) },
      };
    }

    if (typeof value === 'number') {
      cell.numFmt = '#,##0.##';
    } else if (cellNumber && normalizedColumns[cellNumber - 1]?.header?.toLowerCase().includes('date')) {
      cell.numFmt = '@';
    }
  });
};

const buildWorksheet = async ({
  workbook,
  columns = [],
  rows = [],
  sheetName = 'Report',
  brandLabel = 'Foodmandu',
  signatureLabel = 'Signature',
  signatureDate = new Date(),
  assets = {},
} = {}) => {
  const normalizedColumns = Array.isArray(columns) && columns.length ? columns : [
    { header: 'Column', key: 'column' },
  ];
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const worksheet = workbook.addWorksheet(normalizeSheetName(sheetName, 0));

  worksheet.columns = normalizedColumns.map((column) => ({
    key: column.key || undefined,
    width: column.width || Math.max(12, String(column.header || column.key || '').length + 2),
  }));
  worksheet.views = [{ state: 'frozen', ySplit: 3 }];

  const bandRowOne = Array.from({ length: normalizedColumns.length }, () => '');
  const bandRowTwo = Array.from({ length: normalizedColumns.length }, () => '');
  const headerRow = normalizedColumns.map((column) => column.header || column.key || 'Column');
  const dataRows = normalizedRows.map((row) => normalizedColumns.map((column) => {
    const rawValue = typeof column.value === 'function'
      ? column.value(row)
      : row?.[column.key];
    return normalizeCell(rawValue);
  }));

  const bandWorksheetRowOne = worksheet.addRow(bandRowOne);
  const bandWorksheetRowTwo = worksheet.addRow(bandRowTwo);
  const headerWorksheetRow = worksheet.addRow(headerRow);
  const dataWorksheetRows = dataRows.map((row) => worksheet.addRow(row));

  const lastColumnName = encodeColumnName(normalizedColumns.length);
  worksheet.mergeCells(`A1:${lastColumnName}2`);
  bandWorksheetRowOne.height = 34;
  bandWorksheetRowTwo.height = 30;
  const mergedBandCell = worksheet.getCell('A1');
  mergedBandCell.value = `Downloaded: ${formatTimestamp(signatureDate)}`;
  styleBandCell(mergedBandCell);
  styleHeaderRow(headerWorksheetRow);
  dataWorksheetRows.forEach((row, rowIndex) => styleDataRow(row, rowIndex, normalizedColumns));

  if (dataRows.length) {
    worksheet.autoFilter = `A3:${lastColumnName}3`;
  }

  if (assets?.logoBase64) {
    const imageId = workbook.addImage({
      base64: assets.logoBase64,
      extension: 'png',
    });

    worksheet.addImage(imageId, {
      tl: { col: HEADER_LOGO_LEFT_OFFSET, row: HEADER_LOGO_TOP_OFFSET },
      ext: { width: HEADER_LOGO_WIDTH, height: HEADER_LOGO_HEIGHT },
    });
  }

  return worksheet;
};

export const downloadExcelWorkbook = async ({
  fileName = 'report.xlsx',
  sheetName = 'Report',
  columns = [],
  rows = [],
  sheets = null,
  brandLabel = 'Foodmandu',
  signatureLabel = 'Signature',
  signatureDate = new Date(),
} = {}) => {
  if (typeof window === 'undefined') return;

  try {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Foodmandu';
    workbook.created = new Date();
    workbook.modified = new Date();

    const assets = await prepareBrandingAssets();
    const normalizedSheets = Array.isArray(sheets) ? sheets.filter(Boolean) : [];

    if (normalizedSheets.length) {
      for (let index = 0; index < normalizedSheets.length; index += 1) {
        const sheet = normalizedSheets[index];
        const worksheet = await buildWorksheet({
          workbook,
          columns: sheet.columns,
          rows: sheet.rows,
          sheetName: sheet.sheetName || `Sheet ${index + 1}`,
          brandLabel: sheet.brandLabel || brandLabel,
          signatureLabel: sheet.signatureLabel || signatureLabel,
          signatureDate: sheet.signatureDate || signatureDate,
          assets,
        });

        worksheet.name = normalizeSheetName(sheet.sheetName, index);
      }
    } else {
      await buildWorksheet({
        workbook,
        columns,
        rows,
        sheetName,
        brandLabel,
        signatureLabel,
        signatureDate,
        assets,
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    anchor.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export workbook:', error);
  }
};
